let workerInstance = null;
let runSeq = 0;
/** @type {((line: string) => void) | null} */
let deliverStdinLine = null;
/** Active canvas session run — clicks/ticks go here. */
let activeCanvasRun = null;
/** True after game.run() sent {type:'session', active:true}, until stop or run end. */
let canvasSessionActive = false;

function getWorker() {
  if (workerInstance) return workerInstance;
  workerInstance = new Worker(new URL('../workers/pyodideWorker.js', import.meta.url));
  return workerInstance;
}

function resetWorker() {
  if (workerInstance) {
    workerInstance.terminate();
    workerInstance = null;
  }
  deliverStdinLine = null;
  activeCanvasRun = null;
  canvasSessionActive = false;
}

export function deliverInteractiveStdinLine(line) {
  if (typeof deliverStdinLine !== 'function') return false;
  deliverStdinLine(String(line ?? ''));
  deliverStdinLine = null;
  return true;
}

export function isInteractiveStdinReady() {
  return true;
}

export function isCanvasSessionActive() {
  return !!(canvasSessionActive && activeCanvasRun);
}

/** Отправить событие холста в активный run (click / tick / after / stop). */
export function deliverCanvasEvent(event) {
  if (!activeCanvasRun) return false;
  const { worker, runId } = activeCanvasRun;
  try {
    worker.postMessage({
      type: 'canvas_event',
      runId,
      event: event || { type: 'stop' },
    });
    return true;
  } catch {
    return false;
  }
}

function dispatchCanvasCmd(cmd, onCanvas, onTurtle) {
  if (!cmd || typeof cmd !== 'object') return;
  if (cmd.type === 'session') {
    canvasSessionActive = !!cmd.active;
  }
  if (cmd.type === '_control' || cmd.control) {
    const control = cmd.control || cmd.type;
    const detail = {
      type: control === 'start_tick' || control === 'stop_tick' || control === 'after' || control === 'cancel_after'
        ? control
        : String(control),
      ms: cmd.ms,
      id: cmd.id,
    };
    // Если есть onCanvas (конспект) — маршрутизация тиков у потребителя, не в глобальную шину.
    if (onCanvas) {
      onCanvas({ type: '_control', control: detail.type, ms: detail.ms, id: detail.id });
    } else if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('diobuch-canvas-control', { detail }));
    }
    return;
  }
  if (onCanvas) onCanvas(cmd);
  else if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('diobuch-canvas', { detail: cmd }));
  }
  // Устаревший канал — только если нет onCanvas (иначе CodeCanvas получил бы дубль).
  if (!onCanvas) {
    if (onTurtle) onTurtle(cmd);
    else if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('diobuch-turtle', { detail: cmd }));
    }
  }
}

/**
 * @param {string} code - Python code to run
 * @param {string} stdin - Input for input() calls (lines separated by \n), used when interactiveStdin is false
 * @param {Object} files - Optional: { 'input.txt': 'content', ... } - files to create in virtual FS before run
 * @param {Object} options - Optional: { signal?, interactiveStdin?, echoInput?, onStdoutChunk?, onStdinRequest?, onStatus?, onTurtle?, onCanvas?, canvasConfig?, maxStdoutBytes?, maxStdoutChunksPerSec? }
 */
export async function runPython(code, stdin = '', files = {}, options = {}) {
  const runId = ++runSeq;
  const worker = getWorker();
  const onStdoutChunk = typeof options?.onStdoutChunk === 'function' ? options.onStdoutChunk : null;
  const onStdinRequest = typeof options?.onStdinRequest === 'function' ? options.onStdinRequest : null;
  const onStatus = typeof options?.onStatus === 'function' ? options.onStatus : null;
  const onTurtle = typeof options?.onTurtle === 'function' ? options.onTurtle : null;
  const onCanvas = typeof options?.onCanvas === 'function' ? options.onCanvas : null;
  const interactiveStdin = !!options?.interactiveStdin;
  const echoInput = !!options?.echoInput;
  const canvasConfig = options?.canvasConfig && typeof options.canvasConfig === 'object'
    ? options.canvasConfig
    : {};
  const maxStdoutBytes = Number(options?.maxStdoutBytes);
  const maxStdoutChunksPerSec = Number(options?.maxStdoutChunksPerSec);

  activeCanvasRun = { worker, runId };

  return await new Promise((resolve, reject) => {
    const onMessage = (e) => {
      const msg = e.data || {};

      if (msg.type === 'status' && msg.runId === runId) {
        if (onStatus) onStatus(msg.phase || '');
        return;
      }

      if ((msg.type === 'canvas' || msg.type === 'turtle') && msg.runId === runId) {
        dispatchCanvasCmd(msg.cmd, onCanvas, onTurtle);
        return;
      }

      if (msg.type === 'stdout' && msg.runId === runId) {
        if (onStdoutChunk) onStdoutChunk(msg.chunk || '');
        return;
      }

      if (msg.type === 'stdin_request' && msg.runId === runId) {
        deliverStdinLine = (value) => {
          worker.postMessage({
            type: 'stdin_response',
            runId: msg.runId,
            requestId: msg.requestId,
            value: value ?? '',
          });
        };
        if (onStdinRequest) onStdinRequest({ requestId: msg.requestId });
        return;
      }

      if (msg.type !== 'result' || msg.runId !== runId) return;

      worker.removeEventListener('message', onMessage);
      deliverStdinLine = null;
      if (activeCanvasRun?.runId === runId) {
        activeCanvasRun = null;
        canvasSessionActive = false;
      }
      if (msg.success) resolve({ success: true, output: msg.output || '' });
      else resolve({ success: false, error: msg.error || 'Ошибка выполнения', output: msg.output || '' });
    };

    worker.addEventListener('message', onMessage);

    let aborted = false;
    const onAbort = () => {
      aborted = true;
      try {
        worker.postMessage({
          type: 'canvas_event',
          runId,
          event: { type: 'stop' },
        });
      } catch {
        /* ignore */
      }
      worker.removeEventListener('message', onMessage);
      resetWorker();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    if (options?.signal) {
      if (options.signal.aborted) return onAbort();
      options.signal.addEventListener('abort', onAbort, { once: true });
    }

    try {
      const payload = {
        type: 'run',
        runId,
        code,
        stdin,
        files,
        interactiveStdin,
        echoInput,
        canvasConfig,
      };
      if (Number.isFinite(maxStdoutBytes) && maxStdoutBytes > 0) {
        payload.maxStdoutBytes = Math.round(maxStdoutBytes);
      }
      if (Number.isFinite(maxStdoutChunksPerSec) && maxStdoutChunksPerSec > 0) {
        payload.maxStdoutChunksPerSec = Math.round(maxStdoutChunksPerSec);
      }
      worker.postMessage(payload);
    } catch (err) {
      worker.removeEventListener('message', onMessage);
      if (activeCanvasRun?.runId === runId) {
        activeCanvasRun = null;
        canvasSessionActive = false;
      }
      if (!aborted) reject(err);
    }
  });
}
